import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Quote, Sparkles, TrendingUp } from "lucide-react";
import logger from '@/utils/logger';
import { fetchContentJson, extractPanelData, getFieldValue } from "@/utils/contentJsonParser";

interface Testimonial {
  fullname: string;
  companyname: string;
  designation: string;
  TestimonialsText: string;
  ClientImage: { url: string }[];
}

export default function Testimonials() {
  const [currentSet, setCurrentSet] = useState(0);
  const [itemsPerSet, setItemsPerSet] = useState(3);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isHovered, setIsHovered] = useState(false);
  const [sectionTitle, setSectionTitle] = useState<string>("");
  const [sectionPrimaryHeader, setSectionPrimaryHeader] = useState<string>("");
  const [sectionSecondaryHeader, setSectionSecondaryHeader] = useState<string>("");
  const [sectionDescription, setSectionDescription] = useState<string>("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchContentJson();
        
        // Extract the testimonials panel
        const testimonialsPanel = extractPanelData(data, "testimonialsPanel");
        
        if (testimonialsPanel) {
          // Extract section fields
          setSectionTitle(getFieldValue(testimonialsPanel, "testimonials_title", ""));
          setSectionPrimaryHeader(getFieldValue(testimonialsPanel, "testimonials_primary_header", ""));
          setSectionSecondaryHeader(getFieldValue(testimonialsPanel, "testimonials_secondary_header", ""));
          setSectionDescription(getFieldValue(testimonialsPanel, "testimonials_description", ""));

          // Extract testimonials data
          const testimonialsContainer = testimonialsPanel.components?.find(
            (comp: any) => comp.key === "testimonials"
          );
          const employeeInformation = testimonialsContainer?.defaultValue?.employeeInformation;

          if (employeeInformation) {
            // Filter to remove duplicates based on fullname
            const transformedData = employeeInformation
              .filter(
                (employee: any, index: number, self: any[]) =>
                  index === self.findIndex((e) => e.fullname === employee.fullname)
              )
              .map((employee: any) => ({
                ...employee,
                ClientImage: employee.ClientImage || [],
              }));
            setTestimonials(transformedData);
          } else {
            logger.error("No employeeInformation found.");
          }
        }

        setLoading(false);
      } catch (error) {
        logger.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const updateItemsPerSet = () => {
      if (window.innerWidth < 768) {
        setItemsPerSet(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerSet(2);
      } else {
        setItemsPerSet(3);
      }
    };

    updateItemsPerSet();
    window.addEventListener("resize", updateItemsPerSet);
    return () => window.removeEventListener("resize", updateItemsPerSet);
  }, []);

  useEffect(() => {
    if (isHovered) return; // Don't auto-scroll when hovered
    
    const timer = setInterval(() => {
      setCurrentSet((prevSet) =>
        (prevSet + 1) % Math.ceil(testimonials.length / itemsPerSet)
      );
    }, 5000);

    return () => clearInterval(timer);
  }, [itemsPerSet, testimonials.length, isHovered]);

  const currentTestimonials = testimonials.slice(
    currentSet * itemsPerSet,
    (currentSet + 1) * itemsPerSet
  );

  // Group testimonials for display
  const groupedTestimonials: Testimonial[][] = (() => {
    const groups: Testimonial[][] = [];
    for (let i = 0; i < testimonials.length; i += itemsPerSet) {
      groups.push(testimonials.slice(i, i + itemsPerSet));
    }
    return groups;
  })();



  return (
    <section className="py-24 bg-gradient-to-b from-[#e6f9f3] to-white relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary-600/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-forest-custom/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-primary-600/3 to-forest-custom/3 rounded-full blur-3xl"></div>
      </div>

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2310b981' fill-opacity='0.02'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      ></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-primary-100 border border-primary-200 text-primary-700 px-6 py-3 rounded-full text-sm font-medium mb-6">
            <Sparkles className="h-4 w-4 text-primary-600" />
            <span className="text-lg sm:text-xl">{sectionTitle}</span>
            <TrendingUp className="h-4 w-4 text-forest-custom" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight FontStyles mb-3 sm:mb-4">
            <span className="text-gray-900 block">{sectionPrimaryHeader}</span>
            <span className="bg-gradient-to-r from-primary-600 to-forest-custom text-green-600 bg-clip-text block">{sectionSecondaryHeader}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto break-words whitespace-normal lg:whitespace-nowrap">
            {sectionDescription}
          </p>
        </div>

        <div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {currentTestimonials.length > 0 &&
            currentTestimonials.map((testimonial, idx) => (
              <Card
                key={
                  testimonial.fullname +
                  "-" +
                  testimonial.designation +
                  "-" +
                  testimonial.companyname +
                  "-" +
                  idx
                }
                className="group relative bg-white border-2 border-primary-300 hover:border-primary-500 transition-all duration-300 hover:transform hover:scale-105 shadow-lg hover:shadow-xl w-full max-w-[400px] mx-auto rounded-2xl"
              >
                <CardContent className="p-8 relative flex flex-col h-full">
                  {/* Quote icon */}
                  <div className="relative mb-6">
                    <div className="bg-primary-600 w-14 h-14 rounded-full flex items-center justify-center mb-4 shadow-lg">
                      <Quote className="h-7 w-7 text-white" />
                    </div>
                    <div className="flex mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className="h-6 w-6 text-yellow-400 fill-yellow-400 mr-1 drop-shadow-md"
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-900 mb-8 leading-relaxed text-lg font-semibold flex-grow">
                    "{testimonial.TestimonialsText
                      ? testimonial.TestimonialsText.replace(/<[^>]+>/g, "").replace(/^"+|"+$/g, "")
                      : "No testimonial text available."}"
                  </p>
                  <div className="flex items-start relative mt-auto">
                    <div className="rounded-full w-14 h-14 mr-4 shadow-lg ring-2 ring-primary-200 overflow-hidden flex-shrink-0">
                      <img
                        src={
                          testimonial.ClientImage &&
                            testimonial.ClientImage[0] &&
                            testimonial.ClientImage[0].url
                            ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${testimonial.ClientImage[0].url}`
                            : `https://via.placeholder.com/70x70?text=${testimonial.fullname
                              ? testimonial.fullname.charAt(0)
                              : "U"
                            }`
                        }
                        alt={testimonial.fullname || "Unknown"}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            testimonial.fullname || "User"
                          )}&background=3b82f6&color=fff&size=70`;
                        }}
                      />
                    </div>
                    <div className="flex flex-col justify-center min-h-[56px]">
                      <div className="font-bold text-gray-900 text-lg leading-tight mb-1">
                        {testimonial.fullname}
                      </div>
                      <div className="text-sm text-gray-700 font-medium leading-tight">
                        {testimonial.designation}
                        {testimonial.companyname
                          ? `, ${testimonial.companyname}`
                          : ""}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>

        {/* Futuristic Dots Indicator */}
        <div className="flex justify-center mt-16 space-x-3">
          {Array.from({ length: Math.ceil(testimonials.length / itemsPerSet) })
            .map((_, index) => (
              <button
                key={index}
                className={`relative w-4 h-4 rounded-full transition-all duration-300 ${index === currentSet
                    ? "bg-primary-600 ring-4 ring-primary-600/30 shadow-lg shadow-primary-600/50"
                    : "bg-gray-300 hover:bg-gray-400"
                  }`}
                onClick={() => setCurrentSet(index)}
                data-testid={`testimonial-dot-${index}`}
              >
                {index === currentSet && (
                  <div className="absolute inset-0 rounded-full bg-primary-600 animate-ping"></div>
                )}
              </button>
            ))}
        </div>
      </div>
    </section>
  );
}